import './tagGroupComponent.scss';
import React, {useState} from 'react';
import {Button, Form, ListGroupItem} from 'react-bootstrap';
import {SvgIcon} from '../icons/svgIcon';
import {Icons} from '../icons/icons';
import {useTranslation} from 'react-i18next';
import {Aggregation, HibuTag} from '../search/search-types';
import {SelectionType} from '../facets/facetTypes';
import {TagComponent} from './tagComponent';
import {buildDefaultFieldTitle} from '../utils/utils';
import {AggregationComponent} from './aggregationComponent';
import {useLocalizationUtil} from '../localizationUtil';

type TagGroupComponentProps = {
    tagTitle: string;
    selectionType: SelectionType;
    values: HibuTag[] | Aggregation[];
    handleTagSelected?: (tag: HibuTag) => void;
    handleTagDeselected?: () => void;
};

const DEFAULT_TAGS_HIDDEN_LENGTH = 8;

export const TagGroupComponent = React.memo((props: TagGroupComponentProps) => {
    const {t} = useTranslation();
    const [expanded, setExpanded] = useState<boolean>(true);
    const [showMore, setShowMore] = useState(false);
    const {formatNumber} = useLocalizationUtil();

    const onExpand = () => {
        setExpanded(!expanded);
    };

    const renderTagGroupHeader = () => {
        const title = t('fieldTitle.tagsPlural.' + props.tagTitle + '.label', buildDefaultFieldTitle(props.tagTitle));
        const alt = expanded ? t('facets.icon.collapse') : t('facets.icon.expand');

        return (
            <div className="list-group-item-title-wrapper" onClick={onExpand}>
                <div id="tag-group-title" className="list-group-item-title">
                    {props.values.length > 0 && (
                        <Button className="drop-down-button">
                            <SvgIcon
                                icon={Icons.CHEVRON_DOWN}
                                alt={alt}
                                className={expanded ? 'tag-group-expanded' : ''}
                            />
                        </Button>
                    )}
                    {title}
                    <span className="tags-count">
                        {formatNumber(props.values.length)}
                    </span>
                </div>
            </div>
        );
    };

    const renderTagContainer = (values: HibuTag[] | Aggregation[]) => {
        /*
         * The height of the scrollbar is set that 12 items fit in the viewport.
         * In case there are less than 12 items we use a form instead to prevent unnecessary whitespace.
         */
        const MAX_DISPLAY_LENGTH = 12;

        if (values.length <= MAX_DISPLAY_LENGTH) {
            return (
                <Form
                    key={'form-' + props.tagTitle}
                    onSubmit={(event) => event.preventDefault()}
                    className="tag-values"
                >
                    {renderTags(values)}
                </Form>
            );
        } else {
            return <div className={'tag-values'}>{renderTags(values)}</div>;
        }
    };

    const renderTags = (values: HibuTag[] | Aggregation[]) => {
        const elements = values.map((value) => {
            if (value.id) {
                return (
                    <TagComponent
                        key={value.id}
                        tag={value}
                        tagTitle={props.tagTitle}
                        handleTagSelected={props.handleTagSelected}
                        handleTagDeselected={props.handleTagDeselected}
                    />
                );
            } else {
                return <AggregationComponent aggregation={value} />;
            }
        });
        if (showMore) {
            return elements;
        } else {
            return elements.slice(0, DEFAULT_TAGS_HIDDEN_LENGTH);
        }
    };

    const renderShowMore = (remainingTagsCount: number) => {
        return (
            <span className="show-more-tags" onClick={() => setShowMore(!showMore)}>
                {showMore ? t('general.hideAll') : t('general.seeAll', {entry: formatNumber(remainingTagsCount)})}
            </span>
        );
    };

    const remainingTagsCount = props.values.length - DEFAULT_TAGS_HIDDEN_LENGTH;
    return (
        <ListGroupItem className={expanded ? 'expanded' : 'collapsed'}>
            {renderTagGroupHeader()}
            {expanded && renderTagContainer(props.values)}
            {expanded && remainingTagsCount > 0 && renderShowMore(remainingTagsCount)}
        </ListGroupItem>
    );
});
